import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Property, PropertyPricingRule } from '../../../../../features/property/models/property.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogService } from '../../../../services/dialog.service';
import { PropertyPricingRuleService } from '../../../../../features/property/services/property-pricing-rule.service';
import { tap } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AddPropertyPricingForm } from '../add-property-pricing-form/add-property-pricing-form';
import { HapticsService } from '../../../../general/services/haptics.service';
import { SnackBarService } from '../../../../services/snackbar.service';
import { EditPropertyPricingForm } from '../edit-property-pricing-form/edit-property-pricing-form';

@Component({
  selector: 'app-manage-property-pricing',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatTooltipModule
  ],
  templateUrl: './manage-property-pricing.html',
  styleUrl: './manage-property-pricing.scss',
})
export class ManagePropertyPricing implements OnInit {
  private pricingService = inject(PropertyPricingRuleService);
  private haptics = inject(HapticsService);
  private cdr = inject(ChangeDetectorRef);
  private snackbar = inject(SnackBarService);

  property: Property | undefined = undefined;
  dataSource = new MatTableDataSource<PropertyPricingRule>();
  private readonly data = inject<Property>(MAT_DIALOG_DATA);
  private dialogService = inject(DialogService);
  private readonly dialogRef = inject(MatDialogRef<ManagePropertyPricing, Property | null>);
  readonly initial = this.data ?? {};

  ngOnInit() {
    this.property = this.initial;
    this.loadPricingRules();
  }

  loadPricingRules() {
    if(this.property != undefined) {
      this.pricingService.getPropertyPricing(this.property.id).pipe(tap(result => {
        console.log('pricing rules returned', result);
        this.dataSource.data = result;
        this.cdr.detectChanges();
      })).subscribe();
    }
  }

  add() {
    this.haptics.click();
    console.log('add pricing rule');
    const ref = this.dialogService.open(AddPropertyPricingForm, 
      {
        data: this.property,
        width: '800px'
      }
    );

    ref.afterClosed().subscribe(result => {
      console.log('after', result);
      this.loadPricingRules();
    });
  }

  editRule(id: string) {
    this.haptics.click();
    console.log('edit pricing rule');
    const ruleToEdit = this.dataSource.data.find(r => r.id === parseInt(id));
    if (!ruleToEdit || !this.property) {
      console.error('Rule or property not found');
      return;
    }
    console.log('pricing rule to edit', ruleToEdit);
    
    const ref = this.dialogService.open(EditPropertyPricingForm, {
      data: {
        property: this.property,   // <-- Property (needed for propertyId)
        rule: ruleToEdit           // <-- Full rule data to edit
      },
      width: '800px'
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.loadPricingRules();
      }
    });
  }

  removeRule(id: string) {
    this.haptics.click();
    this.dialogService.confirm(
      'This action cannot be undone. Are you sure you want to remove this pricing rule?',
      'Remove Entry',
      'Permanently Remove',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed) {
        console.log('confirmed, delete');
        this.pricingService.deletePricingRule(id).subscribe({
          next: () => {
            this.snackbar.open('Pricing rule deleted successfully!', 'OK', { duration: 5000, panelClass: 'success-snackbar' });
            this.dialogRef.close(true);
          }, 
          error: () => {
            this.snackbar.open('There was problem deleting the pricing rule. Please try again.', 'OK', { duration: 5000, panelClass: 'error-snackbar' });
          }
      })
      }
    });
  }
}
